import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import TextareaAutosize from "react-textarea-autosize";
import { Form, Message, Loader, Grid, Radio } from "semantic-ui-react";

import { BasicFormProps, Media } from "../../../interfaces";
import { useSocialCoachSession, useSocialNetworks } from "../../../utils";

import { VideoProjectUploader } from "../../VideoProjectUploader";

import { SocialNetworkIconButton } from "../../SocialNetworkIconButton";

import { SocialBasicContentRow, SocialRowGrid } from "../../../styling/baseStyle";
import { VideoProjectComponentType, descriptors } from "./descriptors";

import "./styles.scss";
import { StylePreferencesForm } from "../VideoProject/components/StylePreferencesForm";
import { ProjectCaption } from "../VideoProject/components/ProjectCaption";
import { ProjectScheduler } from "../VideoProject/components/ProjectScheduler";

interface OwnProps extends BasicFormProps {
  /**
   * Handle attached file is uploaded
   */
  handleMediaUpdated: (newMedia: Media[]) => void;

  /**
   * Handle datePickers changes
   */
  handleSetFieldValue?: (field: string, value: any) => void;
  handleValidation: (isValid: boolean) => void;
  /**
   * Handle cancel action
   */
  handleCancel?: () => void;

  modeEditable: boolean;
  /**
   * To Indicate if the form is disabled
   */
  disabled: boolean;
  /**
   * To show or hide the Player Custom Hashtags Toggle
   */
  showHashtagToggle: boolean;
}

type Props = OwnProps;
const dataElmId = "playerCreateVideoProject";
/**
 * Represents a PlayerVideoProject content.
 */
const PlayerVideoProjectClass: React.FC<Props> = ({
  handleSetFieldValue,
  handleMediaUpdated,
  handleValidation,
  messages,
  isSubmitting = false,
  values,
  disabled,
  showHashtagToggle,
}) => {
  const { coachId } = useSocialCoachSession();
  const { postableSocialNetworks } = useSocialNetworks();
  const { formatMessage } = useIntl();

  const { socialNetworkMessages, playerId, preferredPostDate } = values;

  const [contentWarnings, setContentWarnings] = useState<string[]>([]);
  const [contentErrors, setContentErrors] = useState<string[]>([]);

  const errors = useMemo(() => {
    return contentErrors.concat(messages?.error || []);
  }, [contentErrors, messages?.error]);

  const warnings = useMemo(() => {
    return contentWarnings.concat(messages?.warn || []);
  }, [contentWarnings, messages?.warn]);

  useEffect(() => {
    handleValidation(errors.length === 0);
  }, [errors]);

  const updateCaption = (value: string) => {
    handleSetFieldValue!("withMessage", value);
    handleSetFieldValue!("messageToPost", value);
  };

  const updateHashtags = (value: string) => {
    handleSetFieldValue!("hashtags", value);
  };

  React.useEffect(() => {
    return () => {
      window.removeEventListener("loadedmetadata", () => {
        // Nothing
      });
    };
  }, []);

  const settingsSection = (): JSX.Element => {
    const { allowShortLink, allowHashtags, allowComplianceDisclaimer } = values;

    const settings: any[] = [];

    if (showHashtagToggle) {
      settings.push({
        label: formatMessage({ ...descriptors[VideoProjectComponentType.allowHashtags] }),
        checked: allowHashtags,
        id: "allowHashtags",
      });
    }

    settings.push({
      label: formatMessage({ ...descriptors[VideoProjectComponentType.allowShortLink] }),
      checked: allowShortLink,
      id: "allowShortLink",
    });

    settings.push({
      label: formatMessage({ ...descriptors[VideoProjectComponentType.allowComplianceDisclaimer] }),
      checked: allowComplianceDisclaimer,
      id: "allowComplianceDisclaimer",
    });

    return (
      <React.Fragment>
        <Grid padded>
          <Grid.Column floated="right" width={10}>
            <Form.Group grouped key={"videoProjectPermissions"} className={"floatedRight"}>
              {settings.map(appSetting => {
                return (
                  <div key={"videoProjectPermissions" + appSetting.id}>
                    <Form.Group key={appSetting.id} className={"floatedRight flex items-center gap-2"}>
                      <label className="small">{appSetting.label}</label>
                      <Radio
                        toggle
                        className={"settingsLevelLabel"}
                        onChange={(e: any, data: any) => {
                          handleSetFieldValue!(data.name, data.checked);
                        }}
                        name={appSetting.id}
                        checked={appSetting.checked}
                      />
                    </Form.Group>
                  </div>
                );
              })}
            </Form.Group>
          </Grid.Column>
        </Grid>
      </React.Fragment>
    );
  };

  /**
   * Return a custom Radio button with Black Style
   * @param id radio identifier
   * @param radioButtonDisabled if the radio is disabled
   * @param checked if the radio is on
   * @param onChange onChange handler
   * @param label Radio Label key on the descriptor file
   * @returns JSX.Element (Radio element)
   */
  const customRadioButton = (
    id: string,
    radioButtonDisabled: boolean | undefined,
    checked: boolean,
    onChange: (e: any, data: any) => void,
    label: keyof typeof VideoProjectComponentType
  ): JSX.Element => {
    return (
      <Radio
        data-elm-id={`${dataElmId}${id}RadioBtn`}
        className={"blackRadio secondary rounded"}
        id={id}
        name={id}
        onChange={onChange}
        checked={checked}
        disabled={radioButtonDisabled}
        label={formatMessage({ ...descriptors[VideoProjectComponentType[label]] })}
      />
    );
  };

  const autoPostRadio = (): JSX.Element => {
    return customRadioButton(
      "isAutoPost",
      false,
      values.isAutoPost,
      (e: any, data: any) => {
        handleSetFieldValue!(`${data.id}`, data.checked);
      },
      "autoPost"
    );
  };

  /*
   * Auto Post or Push Notification
   */
  const deliveryType = () => {
    return (
      <Grid className={"xbigPaddingTop"}>
        <Grid.Row className={"noPadding"}>
          <label>{formatMessage({ ...descriptors[VideoProjectComponentType.postSectionTitle] })}</label>
        </Grid.Row>
        <SocialRowGrid className={"bigPaddingTop"} leftpadding={15} key={"post_row_auto_post"}>
          {autoPostRadio()}
        </SocialRowGrid>

        {values.isAutoPost && (
          <>
            <SocialBasicContentRow className={"socialIconsSection"}>
              {postableSocialNetworks.map((sn, index) => {
                return (
                  <SocialNetworkIconButton
                    disabled={disabled}
                    size={"noMargin"}
                    key={"video_project_post_sn_icon_" + index}
                    active={socialNetworkMessages ? socialNetworkMessages[sn] : false}
                    networkId={sn}
                    customIconClassname={"socialNetworkIconCentered"}
                    onClickHandler={checked => {
                      const snUpdated = { ...socialNetworkMessages, [sn]: checked };
                      const checkedNumber = Object.values(snUpdated).filter(Boolean).length;
                      handleSetFieldValue!("socialNetworkMessages", snUpdated);
                      handleSetFieldValue!("socialNetworksCounter", checkedNumber);
                    }}
                  />
                );
              })}
            </SocialBasicContentRow>
            <SocialBasicContentRow>
              <ProjectScheduler
                deliveryDate={preferredPostDate}
                onChange={(date: Date) => {
                  handleSetFieldValue!("preferredPostDate", date.toISOString());
                }}
              />
            </SocialBasicContentRow>
          </>
        )}

        <SocialRowGrid key={"post_row_push_notification"}>
          {customRadioButton(
            "isPushPost",
            false,
            !values.isAutoPost,
            () => {
              handleSetFieldValue!("isAutoPost", false);
            },
            "pushNotification"
          )}
        </SocialRowGrid>
      </Grid>
    );
  };

  const specialRequestSection = () => {
    return (
      <Form.Field>
        <div className="smallPaddingBottom xbigPaddingTop">
          <label>{formatMessage({ ...descriptors[VideoProjectComponentType.specialRequests] })}</label>
        </div>
        <TextareaAutosize
          data-elm-id={dataElmId + "SpecialRequestInput"}
          id="initialMessages"
          name="initialMessages"
          className="captionField"
          placeholder={formatMessage({ ...descriptors[VideoProjectComponentType.specialRequestsPlaceholder] })}
          rows={3}
          disabled={disabled}
          value={
            values.initialMessages && values.initialMessages.length > 0 ? values.initialMessages[0].content : undefined
          }
          onChange={data => {
            handleSetFieldValue!("initialMessages", [{ content: data.currentTarget.value }]);
          }}
        />
      </Form.Field>
    );
  };

  const preferencesSection = () => {
    return (
      <StylePreferencesForm
        playerId={playerId}
        updatePrefs={prefs => {
          handleSetFieldValue!("preferences", prefs);
        }}
      />
    );
  };

  return (
    <>
      <Form
        size="large"
        error={errors.length > 0}
        warning={warnings.length > 0}
        className={"videoProjectFormContainer"}
        disabled={disabled}
      >
        {isSubmitting && <Loader active size="large" />}

        <div key={"videoProjectFormDiv"} className={disabled ? "newProjectForm disabledForm" : "newProjectForm"}>
          {errors.length > 0 && <Message error list={errors} />}

          {warnings.length > 0 && <Message warning list={warnings} />}

          <Grid columns={2}>
            <Grid.Column>
              {/* Files section */}
              <div className={"attachVideosSection"}>
                <label>{formatMessage({ ...descriptors[VideoProjectComponentType.attachDesc] })}</label>

                <VideoProjectUploader
                  handleMediaUpdated={handleMediaUpdated}
                  originalMedia={values.originalMediaGroup?.media || []}
                />
              </div>

              <ProjectCaption
                playerId={playerId}
                coachId={coachId}
                role={"PLAYER"}
                captionValue={values.messageToPost}
                includePlayerHashtags={values.allowHashtags}
                disabled={disabled}
                onUpdateCaption={v => updateCaption(v)}
                onUpdateHashtags={v => updateHashtags(v)}
                onUpdateErrors={setContentErrors}
                onUpdateWarnings={setContentWarnings}
              />

              {/* Settings */}
              {settingsSection()}

              {/* Post Schedule */}
              {deliveryType()}
            </Grid.Column>
            <Grid.Column className={"rightCol"}>
              {preferencesSection()}
              {specialRequestSection()}
            </Grid.Column>
          </Grid>
        </div>
      </Form>
    </>
  );
};

export const PlayerVideoProjectComponent = PlayerVideoProjectClass;
